import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[11px] font-medium uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        destructive: 'border-transparent bg-destructive text-destructive-foreground',
        outline: 'border-border bg-card/60 text-muted-foreground',
        // Operator status tints — saturated glyph/text on a faint wash of the
        // same hue, with a hairline border. Reads at a glance against the dark.
        active: 'border-status-active/30 bg-status-active/10 text-status-active',
        degraded: 'border-status-degraded/30 bg-status-degraded/10 text-status-degraded',
        failed: 'border-status-failed/30 bg-status-failed/10 text-status-failed',
        inflight: 'border-status-inflight/30 bg-status-inflight/10 text-status-inflight',
        idle: 'border-status-idle/30 bg-status-idle/10 text-status-idle',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps): JSX.Element {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
